/**
 * Hotel profile room/package display SSOT — ILS (PWCCF / displayCurrencies) with USD→ILS FX fallback.
 * Never surfaces ₪0 when authoritative USD > 0; falls back to $ when FX is unavailable.
 */

import { formatHotelSearchAmountForDisplay } from "../constants/hotelCurrencies";
import { formatHotelIlsRoundedDisplay } from "./formatHotelIlsRoundedDisplay";
import { applyFXSettlement } from "../pricing/pricingEngine";

function normalizeCurrency(ccy) {
  return String(ccy ?? "")
    .trim()
    .toUpperCase();
}

function positiveFloat(v) {
  if (v === null || v === undefined) return null;
  const x = typeof v === "number" ? v : parseFloat(String(v));
  if (!Number.isFinite(x) || x <= 0) return null;
  return x;
}

function packagePriceDict(pkg) {
  const pp = pkg?.PackagePrice ?? pkg?.packagePrice;
  return pp && typeof pp === "object" ? pp : {};
}

/** @returns {{ usd: number|null, usdSource: string|null }} */
export function extractAuthoritativeUsdWithSource(pkg) {
  const pd = packagePriceDict(pkg);

  const priceCcy =
    normalizeCurrency(pd.Currency) || normalizeCurrency(pkg?.priceCurrency);
  const supCcy =
    normalizeCurrency(pd.SupplierCurrency) ||
    normalizeCurrency(pkg?.supplierCurrency);

  let finalCustomer = positiveFloat(pd.FinalPrice);
  if (finalCustomer === null) finalCustomer = positiveFloat(pkg?.finalPrice);

  let finalSupplier = positiveFloat(pd.FinalPriceInSupplierCurrency);
  if (finalSupplier === null)
    finalSupplier = positiveFloat(pkg?.finalPriceInSupplierCurrency);

  if (priceCcy === "USD" && finalCustomer !== null) {
    return {
      usd: finalCustomer,
      usdSource: "package.quote_currency_final_price_usd",
    };
  }
  if (supCcy === "USD" && finalSupplier !== null) {
    return {
      usd: finalSupplier,
      usdSource: "package.final_price_in_supplier_currency_usd",
    };
  }

  const otf = pkg?.ourTotalFare ?? pkg?.our_total_fare;
  if (otf && typeof otf === "object") {
    const oc = otf.originalCurrency;
    if (oc && typeof oc === "object") {
      const occ = normalizeCurrency(oc.currency ?? oc.Currency);
      if (occ === "USD") {
        const v = positiveFloat(
          oc.priceWithCommissionAndCreditCardFee ??
            oc.priceWithCommission ??
            oc.finalPrice
        );
        if (v !== null) {
          return {
            usd: v,
            usdSource: "our_total_fare.original_currency_usd",
          };
        }
      }
    }
  }

  for (const key of ["ourPriceUsd", "priceUsd", "priceInUsd"]) {
    const v = positiveFloat(pkg?.[key]);
    if (v !== null) {
      return { usd: v, usdSource: `explicit_field.${key}` };
    }
  }

  return { usd: null, usdSource: null };
}

function positiveAmount(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function fmtGroupedNumber(n, maxDecimals = 2) {
  if (maxDecimals <= 0) {
    return Math.round(n).toLocaleString("en-US");
  }
  const rounded = Number(Number(n).toFixed(maxDecimals));
  if (Math.abs(rounded - Math.round(rounded)) < 1e-9) {
    return Math.round(rounded).toLocaleString("en-US");
  }
  return rounded.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: maxDecimals,
  });
}

/**
 * Profile-context USD→ILS rate (usdToIls(1) SSOT — no duplicate conversion tables).
 * @param {object|null|undefined} profilePriceContext
 * @returns {number|null}
 */
export function resolveProfileUsdToIlsExchangeRate(profilePriceContext) {
  if (!profilePriceContext || typeof profilePriceContext !== "object") return null;

  const direct =
    profilePriceContext.usdToIlsRate ??
    profilePriceContext.exchangeRate ??
    profilePriceContext?.currencyRate?.USD ??
    profilePriceContext?.currencyRate?.usd;
  const directNum = positiveAmount(direct);
  if (directNum != null) return directNum;

  const usdToIls = profilePriceContext.usdToIls;
  if (typeof usdToIls !== "function") return null;

  const fx = applyFXSettlement({
    amount: 1,
    fromCurrency: "USD",
    toCurrency: "ILS",
    usdToIls,
    ilsToUsd: profilePriceContext.ilsToUsd,
  });
  if (fx?.convertedAmount != null && Number.isFinite(Number(fx.convertedAmount))) {
    const rate = Number(fx.convertedAmount);
    return rate > 0 ? rate : null;
  }

  try {
    const one = usdToIls(1, false);
    if (one != null && Number.isFinite(Number(one)) && Number(one) > 0) {
      return Number(one);
    }
  } catch {
    /* ignore */
  }
  return null;
}

function extractDisplayIlsFromPackage(pkg) {
  const dc = pkg?.displayCurrencies;
  if (dc && typeof dc === "object") {
    const fromDc = positiveAmount(dc.ils);
    if (fromDc != null) {
      return { amount: fromDc, source: "display_currencies.ils" };
    }
  }
  const pwccf = positiveAmount(
    pkg?.ourPrice?.priceWithCommissionAndCreditCardFee
  );
  if (pwccf != null) {
    return { amount: pwccf, source: "our_price.priceWithCommissionAndCreditCardFee" };
  }
  return { amount: null, source: null };
}

function formatIlsLine(amountIls) {
  return formatHotelIlsRoundedDisplay(amountIls);
}

function formatUsdLine(amountUsd) {
  if (amountUsd == null || !Number.isFinite(Number(amountUsd)) || Number(amountUsd) <= 0) {
    return "";
  }
  return `($${fmtGroupedNumber(Number(amountUsd), 2)})`;
}

function logHotelRoomRawPriceTrace(payload) {
  if (typeof import.meta === "undefined" || !import.meta.env?.DEV) return;
  try {
    if (typeof console !== "undefined" && console.info) {
      console.info("[HOTEL_ROOM_RAW_PRICE_USD_TO_ILS_TRACE]", payload);
    }
  } catch {
    /* ignore */
  }
}

/**
 * @param {Record<string, unknown>|null|undefined} pkg
 * @param {object|null|undefined} [profilePriceContext]
 * @param {{ roomName?: string|null, packageId?: string|number|null }} [extra]
 * @returns {{
 *   amountIls: number|null,
 *   amountUsd: number|null,
 *   displayAmount: number|null,
 *   displayCurrency: "ILS"|"USD"|null,
 *   exchangeRateUsed: number|null,
 *   sourceCurrency: string|null,
 *   wasConverted: boolean,
 *   fallbackUsed: boolean,
 *   fallbackReason: string|null,
 *   formattedIls: string|null,
 *   formattedUsd: string,
 *   formattedDisplayLine: string|null,
 *   usdSource: string|null,
 *   packageId: string|number|null,
 *   roomName: string|null,
 * }}
 */
export function resolveHotelRoomPackageDisplayPrice(
  pkg,
  profilePriceContext = null,
  extra = {}
) {
  const packageId =
    extra.packageId ??
    pkg?.packageID ??
    pkg?.PackageId ??
    pkg?.packageId ??
    null;
  const roomName = extra.roomName ?? null;

  const ilsPick = extractDisplayIlsFromPackage(pkg);
  const rawIlsBefore = ilsPick.amount;
  let amountIls = rawIlsBefore;
  let sourceCurrency = ilsPick.source;

  const { usd: amountUsd, usdSource } = extractAuthoritativeUsdWithSource(pkg);

  let exchangeRateResolved = null;
  let wasConverted = false;
  let fallbackUsed = false;
  let fallbackReason = null;

  if (amountIls == null && amountUsd != null) {
    exchangeRateResolved = resolveProfileUsdToIlsExchangeRate(profilePriceContext);
    if (exchangeRateResolved != null) {
      amountIls = Number((amountUsd * exchangeRateResolved).toFixed(2));
      wasConverted = true;
      fallbackUsed = true;
      fallbackReason = "usd_to_ils_via_profile_usdToIls";
      sourceCurrency = sourceCurrency ?? usdSource ?? "usd_authoritative";
    }
  }

  let displayAmount = null;
  let displayCurrency = null;

  if (amountIls != null && amountIls > 0) {
    displayAmount = amountIls;
    displayCurrency = "ILS";
  } else if (amountUsd != null && amountUsd > 0) {
    displayAmount = amountUsd;
    displayCurrency = "USD";
    fallbackUsed = true;
    fallbackReason = fallbackReason ?? "display_usd_fx_unavailable";
  }

  const formattedIls = formatIlsLine(amountIls);
  const formattedUsd = formatUsdLine(amountUsd);
  const formattedDisplayLine =
    displayCurrency === "ILS"
      ? formattedIls
      : displayCurrency === "USD"
        ? formatHotelSearchAmountForDisplay(displayAmount, "USD")
        : null;

  const tracePayload = {
    packageId,
    roomName,
    rawUsd: amountUsd,
    rawIlsBefore: rawIlsBefore ?? 0,
    exchangeRateResolved,
    rawIlsAfter: amountIls ?? 0,
    displayAmount,
    displayCurrency,
    fallbackUsed,
    fallbackReason,
  };
  logHotelRoomRawPriceTrace(tracePayload);

  return {
    amountIls: amountIls != null && amountIls > 0 ? amountIls : null,
    amountUsd,
    displayAmount,
    displayCurrency,
    exchangeRateUsed: exchangeRateResolved,
    sourceCurrency,
    wasConverted,
    fallbackUsed,
    fallbackReason,
    formattedIls,
    formattedUsd,
    formattedDisplayLine,
    usdSource,
    packageId,
    roomName,
  };
}

/**
 * Maps room SSOT into legacy displayCurrencies shape for table cells / dev ids.
 * @param {ReturnType<typeof resolveHotelRoomPackageDisplayPrice>} ssot
 */
export function mapRoomDisplayPriceToDisplayCurrencies(ssot) {
  const ils = ssot.amountIls ?? 0;
  const usd = ssot.amountUsd;
  const hasUsd = usd != null && usd > 0;
  return {
    ils,
    usd,
    hasUsd,
    formattedIls:
      ssot.formattedDisplayLine && ssot.displayCurrency === "ILS"
        ? ssot.formattedDisplayLine
        : ils > 0
          ? formatIlsLine(ils)
          : usd != null && usd > 0
            ? formatHotelSearchAmountForDisplay(usd, "USD")
            : "₪ 0",
    formattedUsd: hasUsd ? ssot.formattedUsd || formatUsdLine(usd) : "",
    usdSource: hasUsd ? ssot.usdSource : null,
  };
}
